import {usePlantConfig} from '@domain/shifthandover'
import {ApiClient} from '@hconnect/apiclient'
import {RcfaLinks} from '@hconnect/common/components/runningTimes'
import {StoppageEvent} from '@hconnect/common/stoppages/types/stoppage.types'
import {
  EquipmentData,
  RunningTimeDto as RunningTime,
  StoppageDto as Stoppage,
  RunningTimeStoppage,
  StoppageDto,
  GetStackedBarChartComponent,
  RunningTimeDto,
  Iso8601,
  TimeZone
} from '@hconnect/common/types'
import {FeatureFlagKey, useCommonFeatureFlag} from '@hproduce/feature-flags'
import {useQueryClient} from '@tanstack/react-query'
import {
  getEquipmentStoppageCodesByStoppageType,
  getRunningTimesWithGap,
  StackedBarChart,
  getInitialStoppageData
} from '@widgets/running-times'
import moment from 'moment-timezone'
import {useCallback, FC, useState, useMemo} from 'react'
import {useTranslation} from 'react-i18next'

import {FEATURE_NAMESPACE} from '../consts'

import {PlantRunningTimes} from './PlantRunningTimes'
import {StoppageView} from './StoppageView'

type PlantRunningTimesCardProps = {
  plantId: string
  from: Iso8601
  to: Iso8601
  timezone: TimeZone
  onEquipmentClick: (equipmentId: string) => void
  onStoppagePreviewOpen?: (stoppageId: string) => void
  onStoppageCreate?: (stoppageId: string) => void
  onStoppageEdit?: (stoppageId: string) => void
  onStoppageDelete?: (stoppageId: string) => void
  onStoppageSplit?: (stoppageId: string) => void
}

export const PlantRunningTimesCard: FC<PlantRunningTimesCardProps> = ({
  plantId,
  from,
  to,
  timezone,
  onEquipmentClick,
  onStoppagePreviewOpen,
  onStoppageCreate,
  onStoppageEdit,
  onStoppageDelete,
  onStoppageSplit
}) => {
  const {
    i18n: {language}
  } = useTranslation(FEATURE_NAMESPACE)
  const {data: plantConfig} = usePlantConfig({plantId, language})
  const queryClient = useQueryClient()

  const [stoppageIdToPreview, setStoppageIdToPreview] = useState<string | undefined>(undefined)
  const [showCreateForm, setShowCreateForm] = useState<boolean>(false)
  const [stoppageToPreview, setStoppageToPreview] = useState<StoppageEvent | undefined>(undefined)
  const [stoppageData, setStoppageData] = useState<Partial<Stoppage> | undefined>(undefined)

  const enableMainEquipmentPrefilling = useCommonFeatureFlag(FeatureFlagKey.MainEquipmentPrefill, {
    plantId
  })

  const timeRange = useMemo(
    () => ({
      startDate: moment.utc(from).tz(timezone),
      endDate: moment.utc(moment(to)).tz(timezone)
    }),
    [from, to, timezone]
  )

  const stoppageCodesByType = useMemo(
    () => getEquipmentStoppageCodesByStoppageType(plantConfig),
    [plantConfig]
  )

  const handleStoppageSplit = (stoppage: Partial<Stoppage>) => {
    setStoppageIdToPreview(undefined)
    setStoppageToPreview(undefined)
    setShowCreateForm(true)
    setStoppageData(stoppage)
    stoppageToPreview?.id && onStoppageSplit?.(stoppageToPreview.id)
  }

  const plantDefaultCurrency = plantConfig?.defaultCurrency

  const handleClick = useCallback(
    async (
      rt: RunningTime,
      equipment?: EquipmentData,
      stoppage?: RunningTimeStoppage,
      onUndefinedStoppageClick?: (defaultVal: Partial<StoppageDto>) => void,
      onDefinedStoppageClick?: () => void
    ) => {
      if (stoppage?.cockpitStoppageId) {
        if (!onDefinedStoppageClick) {
          setStoppageIdToPreview(stoppage.cockpitStoppageId)
          onStoppagePreviewOpen?.(stoppage.cockpitStoppageId)
        } else {
          onDefinedStoppageClick()
        }
      } else {
        setStoppageToPreview(undefined)
        if (rt.begin && equipment && plantDefaultCurrency) {
          const defaultValue = await getInitialStoppageData({
            rt,
            equipment,
            plantId,
            queryClient,
            axiosInstance: ApiClient.axiosInstance,
            stoppage,
            enableMainEquipmentPrefilling
          })
          if (!onUndefinedStoppageClick) {
            setStoppageData(defaultValue)
            setShowCreateForm(true)
          } else {
            onUndefinedStoppageClick(defaultValue)
          }
        }
      }
    },
    [
      plantDefaultCurrency,
      onStoppagePreviewOpen,
      plantId,
      queryClient,
      enableMainEquipmentPrefilling
    ]
  )

  const getStackedBarChartComponent: GetStackedBarChartComponent = ({
    equipment,
    runningTimes,
    onUndefinedStoppageClick,
    onDefinedStoppageClick,
    highlightedRunningTimeId
  }) => (
    <StackedBarChart
      timeRange={timeRange}
      timezone={timezone}
      handleStoppageClick={(rt: RunningTime, stoppage?: RunningTimeStoppage) =>
        handleClick(rt, equipment, stoppage, onUndefinedStoppageClick, onDefinedStoppageClick)
      }
      highlightedRunningTimeId={highlightedRunningTimeId}
      data={getRunningTimesWithGap(runningTimes as RunningTimeDto[], timeRange, timezone)}
      equipment={equipment}
      stoppageCodesByType={stoppageCodesByType}
      renderTooltipFooter={({rootCauseFailureAnalyses}) =>
        rootCauseFailureAnalyses && (
          <RcfaLinks plantId={plantId} rootCauseFailureAnalyses={rootCauseFailureAnalyses} />
        )
      }
    />
  )

  return (
    <>
      {stoppageIdToPreview || showCreateForm ? (
        <StoppageView
          plantId={plantId}
          showCreateForm={showCreateForm}
          stoppageData={stoppageData}
          stoppageToPreview={stoppageToPreview}
          setStoppageToPreview={setStoppageToPreview}
          setShowCreateForm={setShowCreateForm}
          stoppageIdToPreview={stoppageIdToPreview}
          setStoppageIdToPreview={setStoppageIdToPreview}
          handleStoppageSplit={handleStoppageSplit}
          onCreate={onStoppageCreate}
          onEdit={onStoppageEdit}
          onDelete={onStoppageDelete}
        />
      ) : (
        <PlantRunningTimes
          plantId={plantId}
          from={from}
          to={to}
          timezone={timezone}
          getStackedBarChartComponent={getStackedBarChartComponent}
          onEquipmentClick={onEquipmentClick}
        />
      )}
    </>
  )
}
